import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Lock, Eye, FileText, BarChart3, Link as LinkIcon, Settings, Mail, Trash2, BookOpen, Images, Database, RefreshCw, Loader2, Cpu, Palette, Target } from 'lucide-react';
import axios from 'axios';
import BlogManagement from '../components/admin/BlogManagement';
import GalleryManagement from '../components/admin/GalleryManagement';
import LLMConfiguration from '../components/admin/LLMConfiguration';
import ThemeManagement from '../components/admin/ThemeManagement';
import SourcingKnowledgeManager from '../components/admin/SourcingKnowledgeManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = () => {
  const { toast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // SEO State
  const [metaTags, setMetaTags] = useState({
    title: '',
    description: '',
    keywords: ''
  });

  const [robotsTxt, setRobotsTxt] = useState('');
  
  // Contact Messages State
  const [messages, setMessages] = useState([]);
  const [messageStats, setMessageStats] = useState({ total: 0, unread: 0, read: 0 });
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Database Seeding State
  const [seedingInProgress, setSeedingInProgress] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  const handleForceSeed = async (replace = false) => {
    const auth = localStorage.getItem('adminAuth');
    setSeedingInProgress(true);
    setSeedResult(null);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/force-seed?replace=${replace}`,
        {},
        { headers: { 'Authorization': `Basic ${auth}` } }
      );
      
      setSeedResult(response.data);
      toast({
        title: "Database Seeded Successfully",
        description: `Seeded: ${response.data.seeded?.join(', ') || 'None'}`,
      });
      
      // Reload the page to show updated data
      window.location.reload();
    } catch (error) {
      console.error('Seeding error:', error);
      toast({
        title: "Seeding Failed",
        description: error.response?.data?.detail || "Failed to seed database",
        variant: "destructive"
      });
    } finally {
      setSeedingInProgress(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Use fixed username "admin" with the entered password
      const auth = btoa(`admin:${password}`);
      const response = await axios.get(`${BACKEND_URL}/api/admin/verify`, {
        headers: {
          'Authorization': `Basic ${auth}`
        }
      });

      if (response.data) {
        setIsAuthenticated(true);
        localStorage.setItem('adminAuth', auth);
        toast({
          title: "Login Successful",
          description: "Welcome to the admin panel",
        });
        loadAdminData(auth);
      }
    } catch (error) {
      toast({
        title: "Login Failed",
        description: "Incorrect password",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAdminData = async (auth) => {
    try {
      // Load meta tags
      const metaResponse = await axios.get(`${BACKEND_URL}/api/admin/seo/meta-tags`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      setMetaTags(metaResponse.data);

      // Load robots.txt
      const robotsResponse = await axios.get(`${BACKEND_URL}/api/admin/robots`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      setRobotsTxt(robotsResponse.data.content);
      
      // Load contact messages and stats
      await loadMessages(auth);
      await loadMessageStats(auth);
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };
  
  const loadMessages = async (auth) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contact/messages`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };
  
  const loadMessageStats = async (auth) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contact/stats`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      setMessageStats(response.data);
    } catch (error) {
      console.error('Error loading message stats:', error);
    }
  };

  const handleUpdateMetaTags = async () => {
    const auth = localStorage.getItem('adminAuth');
    try {
      await axios.put(`${BACKEND_URL}/api/admin/seo/meta-tags`, metaTags, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      toast({
        title: "Success",
        description: "Meta tags updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update meta tags",
        variant: "destructive"
      });
    }
  };

  const handleGenerateSitemap = async () => {
    const auth = localStorage.getItem('adminAuth');
    try {
      await axios.post(`${BACKEND_URL}/api/admin/sitemap/generate`, {}, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      toast({
        title: "Success",
        description: "Sitemap generated successfully at /sitemap.xml",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate sitemap",
        variant: "destructive"
      });
    }
  };

  const handleUpdateRobotsTxt = async () => {
    const auth = localStorage.getItem('adminAuth');
    try {
      await axios.put(`${BACKEND_URL}/api/admin/robots`, { content: robotsTxt }, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      toast({
        title: "Success",
        description: "robots.txt updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update robots.txt",
        variant: "destructive"
      });
    }
  };
  
  const handleMarkAsRead = async (messageId) => {
    const auth = localStorage.getItem('adminAuth');
    try {
      await axios.patch(`${BACKEND_URL}/api/contact/messages/${messageId}/status`, 
        { status: 'read' },
        { headers: { 'Authorization': `Basic ${auth}` } }
      );
      await loadMessages(auth);
      await loadMessageStats(auth);
      toast({
        title: "Success",
        description: "Message marked as read",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update message status",
        variant: "destructive"
      });
    }
  };
  
  const handleMarkAsUnread = async (messageId) => {
    const auth = localStorage.getItem('adminAuth');
    try {
      await axios.patch(`${BACKEND_URL}/api/contact/messages/${messageId}/status`, 
        { status: 'unread' },
        { headers: { 'Authorization': `Basic ${auth}` } }
      );
      await loadMessages(auth);
      await loadMessageStats(auth);
      toast({
        title: "Success",
        description: "Message marked as unread",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update message status",
        variant: "destructive"
      });
    }
  };
  
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }
    
    const auth = localStorage.getItem('adminAuth');
    try {
      await axios.delete(`${BACKEND_URL}/api/contact/messages/${messageId}`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      await loadMessages(auth);
      await loadMessageStats(auth);
      toast({
        title: "Success",
        description: "Message deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete message",
        variant: "destructive"
      });
    }
  };
  
  const filteredMessages = filterStatus === 'all' 
    ? messages 
    : messages.filter(msg => msg.status === filterStatus);

  useEffect(() => {
    const auth = localStorage.getItem('adminAuth');
    if (auth) {
      setIsAuthenticated(true);
      loadAdminData(auth);
    }
  }, []);

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
        <Card className="w-full max-w-md border-none shadow-2xl">
          <CardHeader className="text-center p-4 md:p-6">
            <div className="mx-auto mb-3 md:mb-4 rounded-full bg-amber-100 p-3 md:p-4 w-14 h-14 md:w-16 md:h-16 flex items-center justify-center">
              <Lock className="h-6 w-6 md:h-8 md:w-8 text-amber-600" />
            </div>
            <CardTitle className="text-xl md:text-2xl font-bold">Admin Panel</CardTitle>
            <p className="text-xs md:text-sm text-slate-600">SEO & Content Management</p>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium">Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  autoFocus
                  className="text-base"
                />
              </div>
              <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700" disabled={loading}>
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Admin Panel</h1>
            <p className="text-sm md:text-base text-slate-600">SEO & Content Management for venkatswaroop.in</p>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              setIsAuthenticated(false);
              localStorage.removeItem('adminAuth');
            }}
            className="w-full sm:w-auto"
          >
            Logout
          </Button>
        </div>

        <Tabs defaultValue="messages" className="space-y-4 md:space-y-6">
          {/* Mobile: Horizontal scroll tabs / Desktop: Grid tabs */}
          <TabsList className="flex md:grid md:grid-cols-10 gap-1 md:gap-2 bg-white p-2 overflow-x-auto">
            <TabsTrigger value="messages" className="flex items-center gap-1 md:gap-2 whitespace-nowrap px-3 py-2 text-xs md:text-sm">
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">Messages</span>
              {messageStats.unread > 0 && (
                <span className="rounded-full bg-red-500 px-1.5 md:px-2 py-0.5 text-xs text-white">
                  {messageStats.unread}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="blog" className="flex items-center gap-1 md:gap-2 whitespace-nowrap px-3 py-2 text-xs md:text-sm">
              <BookOpen className="h-4 w-4" />
              <span className="hidden sm:inline">Blog</span>
            </TabsTrigger>
            <TabsTrigger value="sourcing" className="flex items-center gap-1 md:gap-2 whitespace-nowrap px-3 py-2 text-xs md:text-sm">
              <Target className="h-4 w-4" />
              <span className="hidden sm:inline">Sourcing</span>
            </TabsTrigger>
            <TabsTrigger value="gallery" className="flex items-center gap-1 md:gap-2 whitespace-nowrap px-3 py-2 text-xs md:text-sm">
              <Images className="h-4 w-4" />
              <span className="hidden sm:inline">Gallery</span>
            </TabsTrigger>
            <TabsTrigger value="llm" className="flex items-center gap-1 md:gap-2 whitespace-nowrap px-3 py-2 text-xs md:text-sm">
              <Cpu className="h-4 w-4" />
              <span className="hidden sm:inline">AI/LLM</span>
            </TabsTrigger>
            <TabsTrigger value="themes" className="flex items-center gap-1 md:gap-2 whitespace-nowrap px-3 py-2 text-xs md:text-sm">
              <Palette className="h-4 w-4" />
              <span className="hidden sm:inline">Themes</span>
            </TabsTrigger>
            <TabsTrigger value="database" className="flex items-center gap-1 md:gap-2 whitespace-nowrap px-3 py-2 text-xs md:text-sm">
              <Database className="h-4 w-4" />
              <span className="hidden sm:inline">Database</span>
            </TabsTrigger>
            <TabsTrigger value="seo" className="flex items-center gap-1 md:gap-2 whitespace-nowrap px-3 py-2 text-xs md:text-sm">
              <Eye className="h-4 w-4" />
              <span className="hidden sm:inline">SEO</span>
            </TabsTrigger>
            <TabsTrigger value="sitemap" className="flex items-center gap-1 md:gap-2 whitespace-nowrap px-3 py-2 text-xs md:text-sm">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Sitemap</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-1 md:gap-2 whitespace-nowrap px-3 py-2 text-xs md:text-sm">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Analytics</span>
            </TabsTrigger>
          </TabsList>

          {/* Contact Messages */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle>Contact Messages</CardTitle>
                    <p className="text-sm text-slate-600">
                      Manage inquiries from your website
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={filterStatus === 'all' ? 'default' : 'outline'}
                      onClick={() => setFilterStatus('all')}
                      size="sm"
                    >
                      All ({messageStats.total})
                    </Button>
                    <Button
                      variant={filterStatus === 'unread' ? 'default' : 'outline'}
                      onClick={() => setFilterStatus('unread')}
                      size="sm"
                    >
                      Unread ({messageStats.unread})
                    </Button>
                    <Button
                      variant={filterStatus === 'read' ? 'default' : 'outline'}
                      onClick={() => setFilterStatus('read')}
                      size="sm"
                    >
                      Read ({messageStats.read})
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredMessages.length === 0 ? (
                  <div className="py-12 text-center text-slate-500">
                    <Mail className="mx-auto h-12 w-12 opacity-50" />
                    <p className="mt-4">No messages found</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredMessages.map((message) => (
                      <Card 
                        key={message.id} 
                        className={`border ${message.status === 'unread' ? 'border-amber-300 bg-amber-50/50' : 'border-slate-200'}`}
                      >
                        <CardContent className="p-3 md:p-4">
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <div className="flex items-center gap-2">
                                  {message.status === 'unread' && (
                                    <span className="h-2 w-2 rounded-full bg-amber-500 flex-shrink-0"></span>
                                  )}
                                  <h3 className="font-semibold text-slate-900 truncate">{message.name}</h3>
                                </div>
                                <span className="text-xs text-slate-500">
                                  {new Date(message.created_at).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-sm text-slate-600 mb-1 break-all">
                                <strong>Email:</strong> {message.email}
                              </p>
                              <p className="text-sm text-slate-600 mb-2">
                                <strong>Subject:</strong> {message.subject}
                              </p>
                              <p className="text-sm text-slate-700 bg-white p-2 md:p-3 rounded border border-slate-200 break-words">
                                {message.message}
                              </p>
                            </div>
                            <div className="flex md:flex-col gap-2 md:ml-4 flex-shrink-0">
                              {message.status === 'unread' ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleMarkAsRead(message.id)}
                                  className="text-xs flex-1 md:flex-none"
                                >
                                  Mark Read
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleMarkAsUnread(message.id)}
                                  className="text-xs flex-1 md:flex-none"
                                >
                                  Mark Unread
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteMessage(message.id)}
                                className="text-xs"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Blog Management */}
          <TabsContent value="blog">
            <BlogManagement auth={localStorage.getItem('adminAuth')} />
          </TabsContent>

          {/* Sourcing Knowledge Manager */}
          <TabsContent value="sourcing">
            <SourcingKnowledgeManager auth={localStorage.getItem('adminAuth')} />
          </TabsContent>

          {/* Gallery Management */}
          <TabsContent value="gallery">
            <GalleryManagement auth={localStorage.getItem('adminAuth')} />
          </TabsContent>

          {/* LLM Configuration */}
          <TabsContent value="llm">
            <LLMConfiguration auth={localStorage.getItem('adminAuth')} />
          </TabsContent>

          {/* Theme Management */}
          <TabsContent value="themes">
            <ThemeManagement />
          </TabsContent>

          {/* Database Management */}
          <TabsContent value="database">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg md:text-xl">
                  <Database className="h-5 w-5" />
                  Database Management
                </CardTitle>
                <p className="text-sm text-slate-600">
                  Seed and manage database content from seed_data.json
                </p>
              </CardHeader>
              <CardContent className="space-y-4 md:space-y-6">
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 md:p-4">
                  <h3 className="font-semibold text-amber-800 mb-2 text-sm md:text-base">⚠️ Important</h3>
                  <p className="text-xs md:text-sm text-amber-700">
                    Use &ldquo;Force Seed (Replace All)&rdquo; to replace existing blog posts and gallery data with fresh content from seed_data.json. 
                    This will fix formatting issues and update all content.
                  </p>
                </div>

                <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                  <Card className="border-2">
                    <CardContent className="p-4 md:p-6">
                      <h3 className="font-semibold mb-2 text-sm md:text-base">Seed Missing Data Only</h3>
                      <p className="text-xs md:text-sm text-slate-600 mb-4">
                        Only adds data if collections are empty. Won&apos;t replace existing data.
                      </p>
                      <Button 
                        onClick={() => handleForceSeed(false)}
                        disabled={seedingInProgress}
                        variant="outline"
                        className="w-full"
                      >
                        {seedingInProgress ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Seeding...
                          </>
                        ) : (
                          <>
                            <Database className="h-4 w-4 mr-2" />
                            Seed Missing Data
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-red-200">
                    <CardContent className="p-4 md:p-6">
                      <h3 className="font-semibold mb-2 text-red-700 text-sm md:text-base">Force Seed (Replace All)</h3>
                      <p className="text-xs md:text-sm text-slate-600 mb-4">
                        Deletes ALL existing blog posts and gallery data, then re-seeds from seed_data.json.
                      </p>
                      <Button 
                        onClick={() => handleForceSeed(true)}
                        disabled={seedingInProgress}
                        variant="destructive"
                        className="w-full"
                      >
                        {seedingInProgress ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Replacing...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Replace All Data
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                {seedResult && (
                  <div className="rounded-lg bg-slate-100 p-4">
                    <h3 className="font-semibold mb-2">Last Seed Result:</h3>
                    {seedResult.deleted && seedResult.deleted.length > 0 && (
                      <div className="mb-2">
                        <p className="text-sm font-medium text-red-600">Deleted:</p>
                        <ul className="text-sm text-slate-600 list-disc list-inside">
                          {seedResult.deleted.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {seedResult.seeded && seedResult.seeded.length > 0 && (
                      <div className="mb-2">
                        <p className="text-sm font-medium text-green-600">Seeded:</p>
                        <ul className="text-sm text-slate-600 list-disc list-inside">
                          {seedResult.seeded.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {seedResult.skipped && seedResult.skipped.length > 0 && (
                      <div className="mb-2">
                        <p className="text-sm font-medium text-amber-600">Skipped:</p>
                        <ul className="text-sm text-slate-600 list-disc list-inside">
                          {seedResult.skipped.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {seedResult.errors && seedResult.errors.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-red-600">Errors:</p>
                        <ul className="text-sm text-red-500 list-disc list-inside">
                          {seedResult.errors.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SEO Meta Tags */}
          <TabsContent value="seo">
            <Card>
              <CardHeader>
                <CardTitle>Meta Tags Management</CardTitle>
                <p className="text-sm text-slate-600">
                  Edit SEO meta tags for better search engine visibility
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium">Page Title</label>
                  <Input
                    value={metaTags.title}
                    onChange={(e) => setMetaTags({ ...metaTags, title: e.target.value })}
                    placeholder="Page title (50-60 characters)"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Length: {metaTags.title?.length || 0} characters (Recommended: 50-60)
                  </p>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium">Meta Description</label>
                  <Textarea
                    value={metaTags.description}
                    onChange={(e) => setMetaTags({ ...metaTags, description: e.target.value })}
                    placeholder="Meta description (150-160 characters)"
                    rows={3}
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Length: {metaTags.description?.length || 0} characters (Recommended: 150-160)
                  </p>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium">Keywords</label>
                  <Textarea
                    value={metaTags.keywords}
                    onChange={(e) => setMetaTags({ ...metaTags, keywords: e.target.value })}
                    placeholder="Keywords (comma-separated)"
                    rows={2}
                  />
                </div>

                <Button onClick={handleUpdateMetaTags} className="bg-amber-600 hover:bg-amber-700">
                  Update Meta Tags
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sitemap */}
          <TabsContent value="sitemap">
            <Card>
              <CardHeader>
                <CardTitle>Sitemap Management</CardTitle>
                <p className="text-sm text-slate-600">
                  Generate and submit sitemap to search engines
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg bg-slate-100 p-4">
                  <p className="mb-2 text-sm font-medium">Current Sitemap</p>
                  <code className="text-xs text-slate-700">https://venkatswaroop.in/sitemap.xml</code>
                </div>

                <Button onClick={handleGenerateSitemap} className="bg-amber-600 hover:bg-amber-700">
                  Generate Sitemap
                </Button>

                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <p className="text-sm font-medium text-amber-900">Submit to Search Engines</p>
                  <ul className="mt-2 space-y-1 text-xs text-amber-800">
                    <li>• Google: Submit via Google Search Console</li>
                    <li>• Bing: Submit via Bing Webmaster Tools</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics */}
          <TabsContent value="analytics">
            <Card>
              <CardHeader>
                <CardTitle>SEO Analytics</CardTitle>
                <p className="text-sm text-slate-600">
                  Track your website performance and rankings
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:gap-6 grid-cols-2 lg:grid-cols-4">
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
                    <CardContent className="p-4 md:p-6">
                      <p className="text-xs md:text-sm text-slate-600">Page Views</p>
                      <p className="text-2xl md:text-3xl font-bold text-blue-900">-</p>
                      <p className="text-xs text-slate-500 hidden sm:block">Connect Google Analytics</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-green-50 to-green-100">
                    <CardContent className="p-4 md:p-6">
                      <p className="text-xs md:text-sm text-slate-600">Visitors</p>
                      <p className="text-2xl md:text-3xl font-bold text-green-900">-</p>
                      <p className="text-xs text-slate-500 hidden sm:block">Last 30 days</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
                    <CardContent className="p-4 md:p-6">
                      <p className="text-xs md:text-sm text-slate-600">Keywords</p>
                      <p className="text-2xl md:text-3xl font-bold text-purple-900">-</p>
                      <p className="text-xs text-slate-500 hidden sm:block">Ranking keywords</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-amber-50 to-amber-100">
                    <CardContent className="p-4 md:p-6">
                      <p className="text-xs md:text-sm text-slate-600">Backlinks</p>
                      <p className="text-2xl md:text-3xl font-bold text-amber-900">10+</p>
                      <p className="text-xs text-slate-500 hidden sm:block">Quality backlinks</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="mt-4 md:mt-6 rounded-lg border border-blue-200 bg-blue-50 p-3 md:p-4">
                  <p className="text-sm font-medium text-blue-900">Setup Required</p>
                  <p className="mt-1 text-xs text-blue-800">
                    Connect Google Search Console and Google Analytics for detailed insights.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Backlinks */}
          <TabsContent value="backlinks">
            <Card>
              <CardHeader>
                <CardTitle>Backlink Monitor</CardTitle>
                <p className="text-sm text-slate-600">
                  Track high-quality backlinks to your website
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="rounded-lg border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">LinkedIn Profile</p>
                        <p className="text-sm text-slate-600">linkedin.com/in/venkatjyothiswaroop</p>
                      </div>
                      <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
                        Active
                      </span>
                    </div>
                  </div>

                  <div className="rounded-lg border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">WikiFlux Page</p>
                        <p className="text-sm text-slate-600">en.wikiflux.org</p>
                      </div>
                      <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
                        Active
                      </span>
                    </div>
                  </div>

                  <div className="rounded-lg border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Recruiter IQ Platform</p>
                        <p className="text-sm text-slate-600">therecruiteriq.com</p>
                      </div>
                      <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
                        Active
                      </span>
                    </div>
                  </div>

                  <div className="rounded-lg border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">TekWissen Group</p>
                        <p className="text-sm text-slate-600">tekwissen.com</p>
                      </div>
                      <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
                        Active
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
